import { useTranslation } from "react-i18next";
import dottedLeft from "../../assets/dotted_left-corner.png";
import dottedRight from "../../assets/dotted_right_corner.png";
import heroBackground from "../../assets/hero_background.svg";
import confettiBlue from "../../assets/confetti_blue.svg";
import confettiYellow from "../../assets/confetti_yellow.svg";
import confettiPeach from "../../assets/confetti_peach.svg";
import confettiDiamondYellow from "../../assets/confetti_diamond_yellow.svg";
import confettiPurple from "../../assets/confetti_purple.svg";
import confettiRed from "../../assets/confetti_red.svg";
import phone from "../../assets/phone.webp";
import heartEmoji from "../../assets/smiling_face_with_heart.png";
import smilingEmoji from "../../assets/smiling_face_with_hand.png";
import astonishEmoji from "../../assets/astonished_face.png";
import blackEmoji from "../../assets/black_emoji.svg";
import rightCloud from "../../assets/right_cloud.svg";
import leftCloud from "../../assets/left_cloud.svg";
import smartestLiar from "../../assets/ar_smartest_liar_.svg";
import { SmartestLiar } from "../svgs/SmartestLiar";
import trapezium from "../../assets/trapezium.svg";
import lieDeceiveAndWin from "../../assets/ar_lie.svg";
import { LieDeceiveAndWin } from "../svgs/LieDeceiveAndWin";
import { useIsMobile } from "../../hooks/use-mobile";
import mobileHeroBackground from "../../assets/mobile_hero_background.svg";
import mobilePhone from "../../assets/mobile_phone.svg";
import mobileCloudRight from "../../assets/mobile_cloud_right.svg";
import mobileCloudLeft from "../../assets/mobile_cloud_left.svg";
import mobileHeartEmoji from "../../assets/mobile_smiling_face_with_heart_eyes_Left.png";
import { useLandingAuth } from "../../hooks/use-landing-auth";

const buttonClass =
  "bg-hero-yellow text-black px-4 py-1 md:py-2 rounded-lg font-bold border-2 border-black md:text-3xl cursor-pointer hero-button-interactive";

/** Desktop: mirror CTA sides in RTL. Mobile: keep vertical offsets, swap physical sides in RTL. */
const ctaStart =
  "md:left-0 md:-translate-x-1/2 rtl:md:right-0 rtl:md:left-auto rtl:md:translate-x-1/2";
const ctaEnd =
  "md:right-0 md:translate-x-1/2 md:-translate-y-10 rtl:md:left-0 rtl:md:right-auto rtl:md:-translate-x-1/2 rtl:md:-translate-y-10";
const ctaMobileTopEnd = "top-0 -right-10 rtl:-left-10 rtl:right-auto";
const ctaMobileBottomStart = "bottom-0 -left-10 rtl:-right-10 rtl:left-auto";

const heroTitleSlotClass =
  "w-full max-w-[816px] mx-auto min-h-[120px] md:min-h-[220px] flex items-center justify-center";
const heroTitleImgClass =
  "w-full max-h-[200px] md:max-h-[280px] object-contain mx-auto";
const heroTitleSvgWrapClass =
  "w-full flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-[200px] md:[&>svg]:max-h-[280px]";

type Props = {
  landingAuth: ReturnType<typeof useLandingAuth>;
};
export const HeroSection = ({ landingAuth }: Props) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isArabic = i18n.language === "ar";
  const {
    authenticated,
    hasPropaganda,
    createRoom,
    purchase,
    signin,
    navigate,
  } = landingAuth;

  return (
    <section className="relative overflow-hidden bg-hero-orange">
      {/* Corners */}
      <div className="absolute -left-16 -top-10 sm:left-0 sm:top-0">
        <img src={dottedLeft} alt="Dotted Left Corner" />
      </div>

      <div className="absolute -right-10 -top-10 sm:top-0 sm:right-0">
        <img src={dottedRight} alt="Dotted Right Corner" />
      </div>

      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={isMobile ? mobileHeroBackground : heroBackground}
          alt="Hero Background"
          className="object-cover w-full"
        />
      </div>

      <div className="relative z-20 flex flex-col items-center w-full px-4 my-20">
        <div className={`font-bold ${heroTitleSlotClass}`}>
          {isArabic ? (
            <img
              src={lieDeceiveAndWin}
              alt="Lie Deceive And Win"
              className={heroTitleImgClass}
            />
          ) : (
            <div className={heroTitleSvgWrapClass}>
              <LieDeceiveAndWin />
            </div>
          )}
        </div>

        <div
          className="flex bg-no-repeat bg-cover bg-center p-2 w-full max-w-[753px] mx-auto justify-center"
          style={{ backgroundImage: `url(${trapezium})` }}
        >
          <h3 className="text-center text-white md:text-2xl">
            {t("new_hero_banner.the_game")}
          </h3>
        </div>
      </div>

      {/* Confetti */}
      {/* Blue */}
      <img
        src={confettiBlue}
        alt="Confetti Blue"
        className="absolute top-[22%] left-[18%] animate-float-slow z-20 w-3 h-3 md:w-auto md:h-auto"
      />

      {/* Yellow */}
      <img
        src={confettiYellow}
        alt="Confetti Yellow"
        className="absolute top-[28%] right-[18%] animate-float-medium z-20 w-3 h-3 md:w-auto md:h-auto"
      />

      {/* Purple – Top (duplicated in design) */}
      <img
        src={confettiPurple}
        alt="Confetti Purple Top"
        className="absolute top-[18%] right-[35%] animate-float-fast z-20 w-3 h-3 md:w-auto md:h-auto"
      />

      {/* Peach */}
      <img
        src={confettiPeach}
        alt="Confetti Peach"
        className="absolute bottom-[22%] left-[28%] animate-float-fast z-20 w-3 h-3 md:w-auto md:h-auto"
      />

      <img
        src={confettiPeach}
        alt="Confetti Peach Bottom"
        className="absolute bottom-[30%] left-[15%] animate-float-medium z-20 w-3 h-3 md:w-auto md:h-auto"
      />

      {/* Diamond Yellow */}
      <img
        src={confettiDiamondYellow}
        alt="Confetti Diamond Yellow"
        className="absolute bottom-[28%] right-[28%] animate-float-medium z-20 w-3 h-3 md:w-auto md:h-auto"
      />

      {/* Red */}
      <img
        src={confettiRed}
        alt="Confetti Red"
        className="absolute bottom-[18%] right-[12%] animate-float-slow z-20 w-3 h-3 md:w-auto md:h-auto"
      />

      <div className="flex items-center justify-center ">
        <div className="relative flex items-center justify-center w-full max-w-[228px] border-black border-2 rounded-full md:max-w-2xl bg-burgundy">
          <img
            src={isMobile ? mobilePhone : phone}
            alt="Phone"
            className="animate-float-slow"
          />
          {!authenticated && (
            <>
              <button
                type="button"
                data-testid="hero-cta-signup"
                className={`${buttonClass} absolute ${ctaStart} ${isMobile ? ctaMobileTopEnd : ""}`}
                onClick={signin}
              >
                {t("new_hero_banner.signup")}
              </button>
              <button
                type="button"
                data-testid="hero-cta-play"
                className={`${buttonClass} absolute ${ctaEnd} ${isMobile ? ctaMobileBottomStart : ""}`}
                onClick={() => navigate("/join")}
              >
                {t("new_hero_banner.play_now")}
              </button>
            </>
          )}
          {authenticated && !hasPropaganda && (
            <>
              <button
                type="button"
                data-testid="hero-cta-trial"
                className={`${buttonClass} absolute ${ctaStart} ${isMobile ? ctaMobileTopEnd : ""}`}
                onClick={createRoom}
              >
                {t("herobanner.free_trial")}
              </button>
              <button
                type="button"
                data-testid="hero-cta-buy"
                className={`${buttonClass} absolute ${ctaEnd} ${isMobile ? ctaMobileBottomStart : ""}`}
                onClick={() => purchase('main_menu')}
              >
                {t("herobanner.buy")}
              </button>
            </>
          )}
          {authenticated && hasPropaganda && (
            <button
              type="button"
              data-testid="hero-cta-start"
              className={`${buttonClass} absolute ${ctaEnd} ${isMobile ? ctaMobileBottomStart : ""}`}
              onClick={createRoom}
            >
              {t("herobanner.start")}
            </button>
          )}
        </div>
      </div>

      <img
        src={isMobile ? mobileHeartEmoji : heartEmoji}
        alt="Heart Emoji"
        className="absolute z-40 transform left-10 top-[50%] md:translate-x-1/2 bg-white rounded-full md:top-[70%] md:left-[70%] w-10 h-10 md:w-auto md:h-auto animate-float-slow"
      />
      <img
        src={smilingEmoji}
        alt="Smiling Emoji"
        className="absolute z-40 top-[35%] left-[15%] bg-white rounded-full w-10 h-10 md:w-auto md:h-auto animate-float-medium"
      />
      <img
        src={astonishEmoji}
        alt="Astonished Emoji"
        className={`absolute z-40 md:top-[15%] md:right-[20%] bg-white rounded-full w-10 h-10 md:w-auto md:h-auto animate-float-fast ${isMobile ? "bottom-[12%] right-[10%]" : ""}`}
      />
      <img
        src={blackEmoji}
        alt="Black Emoji"
        className="absolute z-40 top-[30%] right-0 w-10 h-10 md:w-auto md:h-auto animate-pulse"
      />

      <img
        src={isMobile ? mobileCloudRight : rightCloud}
        alt="Right Cloud"
        className="absolute right-0 z-10 top-[70%]"
      />
      <img
        src={isMobile ? mobileCloudLeft : leftCloud}
        alt="Left Cloud"
        className="absolute left-0 z-10 top-[90%] md:top-[70%]"
      />

      <div className="flex flex-col items-center justify-end h-full">
        <div className={`font-bold ${heroTitleSlotClass}`}>
          {isArabic ? (
            <img
              src={smartestLiar}
              alt="Smartest Liar"
              className={heroTitleImgClass}
            />
          ) : (
            <div className={heroTitleSvgWrapClass}>
              <SmartestLiar />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
